import { useState, useEffect } from 'react';
import { 
  Users, 
  MessageSquare, 
  ShoppingCart, 
  TrendingUp,
  Activity,
  Clock,
  Calendar,
  User,
  Coffee
} from 'lucide-react';
import { Link } from 'react-router-dom';

interface Stats {
  totalUsers: number;
  totalMessages: number;
  totalLists: number;
  activeLists: number;
}

interface AttendanceHistory {
  id: string;
  user_id: string;
  clock_in: string;
  clock_out: string | null;
  total_hours: number | null;
  status: string;
  created_at: string;
}

interface AttendanceBreak {
  id: string;
  attendance_id: string;
  break_start: string;
  break_end: string | null;
  duration_minutes: number | null;
  reason: string;
}

interface Task {
  id: string;
  title: string;
  description: string;
  assigned_to: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  due_date: string | null;
  created_at: string;
}

const Dashboard = () => {
  const [stats, setStats] = useState<Stats>({
    totalUsers: 0,
    totalMessages: 0,
    totalLists: 0,
    activeLists: 0
  });
  const [loading, setLoading] = useState(true);
  const [backendAvailable, setBackendAvailable] = useState(false);
  
  // Estados de atendimento
  const [history, setHistory] = useState<AttendanceHistory[]>([]);
  const [breaks, setBreaks] = useState<{ [key: string]: AttendanceBreak[] }>({});
  const [userName, setUserName] = useState('');
  const [filter, setFilter] = useState({
    start_date: '',
    end_date: ''
  });

  // Estados de tarefas
  const [tasks, setTasks] = useState<Task[]>([]);

  useEffect(() => {
    loadDashboardData();
    loadUserInfo();
    loadHistory();
    loadTasks();
  }, []);

  useEffect(() => {
    loadHistory();
  }, [filter]);

  const loadDashboardData = async () => {
    try {
      // Test if backend is available
      const token = localStorage.getItem('token');
      const authHeader: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};

      const healthResponse = await fetch('/api/health', { headers: authHeader });
      if (healthResponse.ok) {
        setBackendAvailable(true);
        
        // Load stats
        const statsResponse = await fetch('/api/stats', { headers: authHeader });
        if (statsResponse.ok) {
          const statsData = await statsResponse.json();
          setStats(statsData.data);
        }
      } else {
        setBackendAvailable(false);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setBackendAvailable(false);
    } finally {
      setLoading(false);
    }
  };

  const loadUserInfo = async () => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        setUserName(user.name || user.email);
      }
    } catch (error) {
      console.error('Erro ao carregar usuário:', error);
    }
  };

  const loadHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || window.location.origin;
      const params = new URLSearchParams();
      
      if (filter.start_date) params.append('start_date', filter.start_date);
      if (filter.end_date) params.append('end_date', filter.end_date);

      const res = await fetch(`${apiUrl}/api/attendance/history?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      
      if (data.success) {
        setHistory(data.data);
        loadBreaksForAttendances(data.data);
      }
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
    }
  };

  const loadBreaksForAttendances = async (attendances: AttendanceHistory[]) => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || window.location.origin;
      const breaksData: { [key: string]: AttendanceBreak[] } = {};

      for (const attendance of attendances) {
        const res = await fetch(`${apiUrl}/api/attendance/breaks/${attendance.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        if (data.success) {
          breaksData[attendance.id] = data.data;
        }
      }

      setBreaks(breaksData);
    } catch (error) {
      console.error('Erro ao carregar pausas:', error);
    }
  };

  const calculateStats = () => {
    const totalHours = history.reduce((sum, item) => sum + (parseFloat(item.total_hours?.toString() || '0')), 0);
    const avgHours = history.length > 0 ? totalHours / history.length : 0;
    const totalDays = history.length;
    
    const totalBreakMinutes = Object.values(breaks).flat().reduce((sum, breakItem) => {
      return sum + (breakItem.duration_minutes || 0);
    }, 0);
    const totalBreakHours = totalBreakMinutes / 60;
    
    return {
      totalHours: totalHours.toFixed(2),
      avgHours: avgHours.toFixed(2),
      totalDays,
      totalBreakHours: totalBreakHours.toFixed(2)
    };
  };

  const getTotalBreakTime = (attendanceId: string) => {
    const attendanceBreaks = breaks[attendanceId] || [];
    const totalMinutes = attendanceBreaks.reduce((sum, b) => sum + (b.duration_minutes || 0), 0);
    if (totalMinutes === 0) return '-';
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return hours > 0 ? `${hours}h ${minutes}min` : `${minutes}min`;
  };

  const formatDate = (dateString: string) => {
    const dateStr = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  };

  const formatTime = (dateString: string) => {
    const dateStr = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('pt-BR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Ativo</span>;
      case 'break':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">Em Pausa</span>;
      case 'finished':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">Finalizado</span>;
      default:
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  const loadTasks = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || window.location.origin;
      
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;
      const isAdmin = user?.role === 'admin';

      const endpoint = isAdmin ? '/api/tasks' : '/api/tasks/my-tasks';

      const res = await fetch(`${apiUrl}${endpoint}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      
      if (data.success) {
        // Filtrar apenas tarefas pendentes e em andamento, e mostrar as 5 mais recentes
        const activeTasks = data.data.filter((task: Task) => 
          task.status === 'pending' || task.status === 'in_progress'
        );
        setTasks(activeTasks.slice(0, 5));
      }
    } catch (error) {
      console.error('Erro ao carregar tarefas:', error);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTaskStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">Pendente</span>;
      case 'in_progress':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">Em Andamento</span>;
      case 'completed':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Concluída</span>;
      case 'cancelled':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">Cancelada</span>;
      default:
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  const StatCard = ({ title, value, icon: Icon, color, trend, currency = false }: any) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">
            {currency && value > 0 ? `$${value.toLocaleString()}` : value.toLocaleString()}
          </p>
          {trend && (
            <p className="text-sm text-green-600 mt-1 flex items-center">
              <TrendingUp className="h-4 w-4 mr-1" />
              {trend}
            </p>
          )}
        </div>
        <div className={`p-3 rounded-full ${color}`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
      </div>
    </div>
  );


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-1">Overview of your ChatBot CRM system</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Users"
          value={stats.totalUsers}
          icon={Users}
          color="bg-blue-500"
          trend={backendAvailable ? "+12% this month" : null}
        />
        <StatCard
          title="Messages Sent"
          value={stats.totalMessages}
          icon={MessageSquare}
          color="bg-green-500"
          trend={backendAvailable ? "+8% this week" : null}
        />
        <StatCard
          title="Shopping Lists"
          value={stats.totalLists}
          icon={ShoppingCart}
          color="bg-purple-500"
          trend={backendAvailable ? "+15% this month" : null}
        />
        <StatCard
          title="Active Lists"
          value={stats.activeLists}
          icon={Activity}
          color="bg-orange-500"
        />
      </div>

      {/* Estatísticas de Atendimento */}
      <div className="mt-8">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Estatísticas de Atendimento</h2>
          <p className="text-gray-600">
            {userName && <span className="font-semibold">{userName}</span>} - Acompanhe seu histórico de expediente e pausas
          </p>
        </div>

        {/* Cards de Resumo de Atendimento */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total de Horas</p>
                <p className="text-2xl font-bold text-gray-900">{calculateStats().totalHours}h</p>
              </div>
              <Clock className="h-12 w-12 text-blue-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Média por Dia</p>
                <p className="text-2xl font-bold text-gray-900">{calculateStats().avgHours}h</p>
              </div>
              <TrendingUp className="h-12 w-12 text-green-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Dias Trabalhados</p>
                <p className="text-2xl font-bold text-gray-900">{calculateStats().totalDays}</p>
              </div>
              <Calendar className="h-12 w-12 text-purple-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total de Pausas</p>
                <p className="text-2xl font-bold text-gray-900">{calculateStats().totalBreakHours}h</p>
              </div>
              <Coffee className="h-12 w-12 text-yellow-500" />
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-xl font-semibold mb-4">Filtrar Período</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Data Início</label>
              <input
                type="date"
                value={filter.start_date}
                onChange={(e) => setFilter({ ...filter, start_date: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Data Fim</label>
              <input
                type="date"
                value={filter.end_date}
                onChange={(e) => setFilter({ ...filter, end_date: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>

            <div className="flex items-end">
              <button
                onClick={() => setFilter({ start_date: '', end_date: '' })}
                className="w-full bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600"
              >
                Limpar Filtros
              </button>
            </div>
          </div>
        </div>

        {/* Tabela de Histórico */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-xl font-semibold">Histórico de Expediente</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entrada</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Saída</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pausas</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total de Horas</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {history.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                      <User className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                      <p>Nenhum registro encontrado</p>
                    </td>
                  </tr>
                ) : (
                  history.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(item.clock_in)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatTime(item.clock_in)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.clock_out ? formatTime(item.clock_out) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-yellow-600 font-medium">
                        {getTotalBreakTime(item.id)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {item.total_hours ? `${parseFloat(item.total_hours.toString()).toFixed(2)}h` : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(item.status)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Tarefas Pendentes */}
      {tasks.length > 0 && (
        <div className="mt-8">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Minhas Tarefas</h2>
              <p className="text-gray-600">Tarefas pendentes e em andamento</p>
            </div>
            <Link 
              to="/tarefas"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Ver Todas
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tasks.map((task) => (
              <div key={task.id} className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-lg font-semibold text-gray-900 flex-1">{task.title}</h3>
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(task.priority)}`}>
                    {task.priority === 'urgent' ? 'Urgente' : 
                     task.priority === 'high' ? 'Alta' : 
                     task.priority === 'medium' ? 'Média' : 'Baixa'}
                  </span>
                </div>

                <p className="text-sm text-gray-600 mb-4 line-clamp-2">{task.description}</p>

                <div className="flex items-center justify-between">
                  {getTaskStatusBadge(task.status)}
                  {task.due_date && (
                    <div className="flex items-center text-sm text-gray-500">
                      <Clock className="h-4 w-4 mr-1" />
                      {new Date(task.due_date).toLocaleDateString('pt-BR')}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;